import { useApolloClient, useQuery } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { appLinks } from 'src/lib/routing';
import type { FindDocumentTemplateId, LoadDocumentState } from 'types/graphql';
import { useCreateDocumentTemplate, useUpdateDocumentTemplate } from '..';

import DocumentTemplateApi from '../api/DocumentTemplate.api';
import createDocumentStoragePlugin from '../plugins/documentStorage';

/** Template ids are unique per (organization, entityType, name); the org is the session's. */
const templateKey = (entityType: unknown, name: unknown) => `${String(entityType)}\u0000${String(name)}`;

/**
 * Implements temp local storage and remote document storage
 *
 * TODO: Add TTL for local storage
 * TODO: Implement isNew flag in documentStorage plugin
 *
 */
export default function useDocumentStorage({
  isNew,
  documentConfig,
  setStorageSaveModalOpen,
  setStorageLoadModalOpen,
  setTemplateId,
  setShowInBulkPrintMenu,
}: {
  isNew: boolean;
  documentConfig: { name?: string; entity?: string; type?: string };
  setStorageSaveModalOpen: (open: boolean) => void;
  setStorageLoadModalOpen: (open: boolean) => void;
  setTemplateId: (id: number | null | undefined) => void;
  setShowInBulkPrintMenu: (show: boolean) => void;
}) {
  const client = useApolloClient();
  const { create: createTemplate } = useCreateDocumentTemplate();
  const { update: updateTemplate } = useUpdateDocumentTemplate();
  const navigate = useNavigate();

  const documentConfigRef = useRef<{ name?: string; type?: string; entity?: string }>(documentConfig);
  const knownTemplateIds = useRef(new Map<string, number>());

  const {
    data: templateData,
    variables: loadedVariables,
    refetch: loadDocument,
  } = useQuery<LoadDocumentState>(DocumentTemplateApi.LoadDocumentTemplateState, {
    // `documentTemplate(name:, entityType:)` requires both; without them there is no saved template to read.
    skip: isNew || !documentConfigRef.current?.name || !documentConfigRef.current?.entity,
    variables: { name: documentConfigRef.current?.name, entityType: documentConfigRef.current?.entity },
  });

  useEffect(() => {
    documentConfigRef.current = documentConfig;
  }, [documentConfig]);

  // Template metadata must be seeded on EVERY load path, not just loadRemote — the storage
  // plugin short-circuits to localStorage and skips loadRemote entirely.
  useEffect(() => {
    if (!templateData?.documentTemplate) {
      return;
    }

    knownTemplateIds.current.set(
      templateKey(loadedVariables?.entityType, loadedVariables?.name),
      templateData.documentTemplate.id
    );
    setTemplateId(templateData.documentTemplate.id);
    setShowInBulkPrintMenu(!!templateData.documentTemplate.showInBulkPrintMenu);
  }, [templateData, loadedVariables, setTemplateId, setShowInBulkPrintMenu]);

  /** The id saved under this name, from the load read or an earlier save; one by-name read the first time only. */
  const resolveTemplateId = useCallback(
    async (name: string, entityType: string) => {
      const known = knownTemplateIds.current.get(templateKey(entityType, name));
      if (known !== undefined) return known;
      const { data: found } = await client.query<FindDocumentTemplateId>({
        query: DocumentTemplateApi.FindDocumentTemplateId,
        variables: { name, entityType },
        fetchPolicy: 'no-cache',
      });
      return found?.documentTemplate?.id ?? undefined;
    },
    [client]
  );

  const plugin = useMemo(
    () =>
      createDocumentStoragePlugin({
        setStorageSaveModalOpen,
        setStorageLoadModalOpen,
        saveRemote: async (saveState, pages, metadata) => {
          if (!documentConfigRef.current) {
            throw new Error('Document config not set');
          }

          const fields = {
            templateState: JSON.stringify(saveState),
            template: { pages },
            type: metadata.type,
            generationOptions: metadata.generationOptions,
            showInBulkPrintMenu: metadata.showInBulkPrintMenu,
          };
          const existingId = await resolveTemplateId(metadata.name, metadata.entity);
          const saved =
            existingId === undefined
              ? (await createTemplate({ input: { name: metadata.name, entityType: metadata.entity, ...fields } })).data
                  ?.createDocumentTemplate
              : (await updateTemplate({ id: existingId, input: fields })).data?.updateDocumentTemplate;
          if (!saved) {
            throw new Error('Document template save returned no template');
          }
          knownTemplateIds.current.set(templateKey(metadata.entity, metadata.name), saved.id);

          void navigate(
            appLinks.documentEditor(
              { name: metadata.name, entity: metadata.entity, type: metadata.type },
              { replace: true }
            )
          );

          return saved;
        },
        loadRemote: async () => {
          const { name, entity } = documentConfigRef.current;
          if (!name || !entity) {
            return null;
          }

          const { data } = await loadDocument({ name, entityType: entity });

          if (!data) {
            return null;
          }

          setTemplateId(data.documentTemplate?.id);
          setShowInBulkPrintMenu(!!data.documentTemplate?.showInBulkPrintMenu);

          const template = data.documentTemplate?.templateState;

          if (!template) {
            return null;
          }

          return JSON.parse(String(template));
        },
        documentConfig: documentConfigRef,
      }),
    [
      resolveTemplateId,
      createTemplate,
      loadDocument,
      navigate,
      updateTemplate,
      setTemplateId,
      setShowInBulkPrintMenu,
      setStorageSaveModalOpen,
      setStorageLoadModalOpen,
    ]
  );

  return { plugin };
}
